import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import api from '../../lib/api';
import { track } from '../../lib/analytics';
import Button from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { cn, formatPercentOneDecimal } from '../../lib/utils';

type Row = {
  kolId: string;
  name: string;
  invitationCount: number;
  acceptanceRatePct: number;
  submissionCount: number;
  approvalRatePct: number;
  revisionCount: number;
  scriptApprovalRatePct: number;
};

async function fetchKolPerformance(): Promise<{ kols: Row[]; agencyOrgId: string }> {
  const me = await api.get('/auth/me');
  const agency = me.data.memberships?.find((m: { organization?: { type?: string } }) => m.organization?.type === 'AGENCY');
  const oid = agency?.organizationId || '';
  if (!oid) return { kols: [], agencyOrgId: '' };
  const res = await api.get('/analytics/kol-performance', { params: { agencyOrgId: oid } });
  return { kols: res.data.kols || [], agencyOrgId: oid };
}

export default function KolPerformanceReport() {
  const queryClient = useQueryClient();
  const kolPerfQuery = useQuery({
    queryKey: ['agency', 'kol-performance'],
    queryFn: fetchKolPerformance,
    staleTime: 15 * 60 * 1000,
  });

  const rows = kolPerfQuery.data?.kols ?? [];
  const agencyOrgId = kolPerfQuery.data?.agencyOrgId ?? '';
  const reloading = kolPerfQuery.isFetching && !kolPerfQuery.isPending;

  const chartData = useMemo(
    () =>
      rows.slice(0, 12).map((r) => ({
        name: r.name.length > 14 ? `${r.name.slice(0, 12)}…` : r.name,
        acceptance: r.acceptanceRatePct,
        approval: r.approvalRatePct,
        scriptOK: r.scriptApprovalRatePct,
      })),
    [rows]
  );

  const exportCsv = async () => {
    if (!agencyOrgId) return;
    const res = await api.get('/analytics/kol-performance/export.csv', {
      params: { agencyOrgId },
      responseType: 'blob',
    });
    const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'kol-performance.csv';
    a.click();
    URL.revokeObjectURL(url);
    track('export_downloaded', {
      endpoint: '/analytics/kol-performance/export.csv',
      filename: 'kol-performance.csv',
    });
  };

  if (kolPerfQuery.isPending) {
    return <div className="p-8 text-glass-text-3">Loading…</div>;
  }

  return (
    <div className="space-y-6 max-w-6xl">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="page-title">KOL performance</h1>
          <p className="text-glass-text-2 text-sm mt-1">
            Aggregated invitation, script, and submission outcomes per talent.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={() => queryClient.invalidateQueries({ queryKey: ['agency', 'kol-performance'] })}
            disabled={kolPerfQuery.isFetching}
          >
            <RefreshCw className={cn('h-4 w-4 mr-2', reloading && 'animate-spin')} />
            Reload data
          </Button>
          <Button variant="outline" onClick={exportCsv} disabled={!agencyOrgId}>
            Export CSV
          </Button>
        </div>
      </div>

      <Card className="p-5">
        <CardHeader>
          <CardTitle>Acceptance & approval rates (%)</CardTitle>
        </CardHeader>
        <CardContent className="h-80">
          {chartData.length === 0 ? (
            <p className="text-sm text-glass-text-3">No KOLs in roster yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="rgb(var(--glass-border) / 0.2)"
                />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11, fill: 'rgb(var(--glass-text-secondary))' }}
                  stroke="rgb(var(--glass-border) / 0.3)"
                />
                <YAxis
                  domain={[0, 100]}
                  tick={{ fontSize: 11, fill: 'rgb(var(--glass-text-secondary))' }}
                  stroke="rgb(var(--glass-border) / 0.3)"
                />
                <Tooltip
                  formatter={(value: number) => formatPercentOneDecimal(value)}
                  contentStyle={{
                    background: 'rgb(var(--glass-surface-1) / 0.95)',
                    border: '1px solid rgb(var(--glass-border) / 0.2)',
                    borderRadius: 12,
                    color: 'rgb(var(--glass-text-primary))',
                  }}
                />
                <Legend wrapperStyle={{ color: 'rgb(var(--glass-text-secondary))' }} />
                <Bar dataKey="acceptance" name="Invite accept %" fill="rgb(var(--accent-rgb))" />
                <Bar dataKey="approval" name="Content approval %" fill="#22c55e" />
                <Bar dataKey="scriptOK" name="Script approved %" fill="#a855f7" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <Card className="p-5">
        <CardHeader>
          <CardTitle>Detail table</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-glass text-left text-glass-text-2 uppercase tracking-[0.05em] text-xs">
                <th className="py-2 pr-4 font-medium">Name</th>
                <th className="py-2 pr-4 font-medium">Invites</th>
                <th className="py-2 pr-4 font-medium">Accept %</th>
                <th className="py-2 pr-4 font-medium">Submissions</th>
                <th className="py-2 pr-4 font-medium">Content appr. %</th>
                <th className="py-2 pr-4 font-medium">Revisions</th>
                <th className="py-2 pr-4 font-medium">Script appr. %</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.kolId}
                  className="border-b border-glass last:border-0 text-glass-text"
                >
                  <td className="py-2 pr-4 font-medium">{r.name}</td>
                  <td className="py-2 pr-4">{r.invitationCount}</td>
                  <td className="py-2 pr-4">
                    {formatPercentOneDecimal(r.acceptanceRatePct)}
                  </td>
                  <td className="py-2 pr-4">{r.submissionCount}</td>
                  <td className="py-2 pr-4">
                    {formatPercentOneDecimal(r.approvalRatePct)}
                  </td>
                  <td className="py-2 pr-4">{r.revisionCount}</td>
                  <td className="py-2 pr-4">
                    {formatPercentOneDecimal(r.scriptApprovalRatePct)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
